import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ImpersonationBroadcastService } from './impersonation-broadcast.service';
import { environment } from '../../../environments/environment';
import { leaveImpersonationHost } from '../utils/vanity-url.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private isHandlingImpersonationEnd = false;

  constructor(private authService: AuthService, private impersonationBroadcast: ImpersonationBroadcastService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const modifiedRequest = req.clone({ headers: req.headers.set('x-functions-key', environment.functionKey) });

    return next.handle(modifiedRequest).pipe(
      catchError((error) => {
        const url = req.url.toLowerCase();
        const isAuthEndpoint = url.includes('/login') || url.includes('/refresh') || url.includes('/logout');

        let errorBody = error?.error;

        // Azure Functions can return the JSON body as a string.
        if (typeof errorBody === 'string') {
          try {
            errorBody = JSON.parse(errorBody);
          } catch {
            errorBody = null;
          }
        }

        if (error.status === 409 && errorBody?.errorCode === 'ImpersonationEnded') {
          if (!this.isHandlingImpersonationEnd) {
            this.isHandlingImpersonationEnd = true;
            this.impersonationBroadcast.notifyChanged();
            leaveImpersonationHost();
          }
          return throwError(() => error);
        }

        if (error.status === 401 && !this.isRefreshing && !isAuthEndpoint) {
          this.isRefreshing = true;
          return this.authService.refreshToken().pipe(
            switchMap(() => next.handle(modifiedRequest)),
            catchError((refreshError) => {
              // Automatic sign-out on a failed refresh is intentionally disabled: let the failing
              // request surface its 401 instead of bouncing the user to the sign-in page. The user
              // stays put and can retry / re-authenticate manually. (Re-enable by calling
              // this.authService.logout() here once refresh reliability is confirmed.)
              return throwError(() => refreshError);
            }),
            finalize(() => {this.isRefreshing = false;}),
          );
        }

        return throwError(() => error);
      }),
    );
  }
}